/* eslint-disable react/no-unescaped-entities */

import { NextSeo } from "next-seo";

import ComparisonSection from "@/components/marketing/ComparisonSection";
import FAQSection from "@/components/marketing/FAQSection";
import Footer from "@/components/marketing/pieces/Footer";
import Navbar from "@/components/marketing/pieces/Navbar";
import PricingHeroSection from "@/components/marketing/PricingHeroSection";
import ReviewsSection from "@/components/marketing/ReviewsSection";
import { SUBSCRIPTION_PLANS, VOICE_AGENT_ADDON } from "@/lib/constants/pricing";

import type { ComparisonGroup, ComparisonSummary, Competitor } from "@/components/marketing/ComparisonSection";
import type { FAQItem } from "@/components/marketing/FAQSection";

// ---------------------------------------------------------------------------
// comparison table data
// ---------------------------------------------------------------------------

const COMPETITORS: Competitor[] = [
  { name: "Marketing Agencies", priceTier: "$$$$" },
  { name: "DIY Website Builders", priceTier: "$" },
  { name: "Home Service CRMs", priceTier: "$$$" },
];

const COMPARISON_GROUPS: ComparisonGroup[] = [
  {
    label: "Get More Leads",
    features: [
      { name: "AI website built for home services", availability: [true, false, false] },
      { name: "Google Business Profile optimization", availability: [true, false, false] },
      { name: "SEO pages for every city you serve", availability: [true, false, false] },
      { name: "Project pages from completed jobs", availability: [false, false, false] },
      { name: "Social media from job photos", availability: [true, false, false] },
    ],
  },
  {
    label: "Automate Communication",
    features: [
      { name: "Automatic lead follow-ups", availability: [false, false, true] },
      { name: "Automatic quote follow-ups", availability: [false, false, false] },
      { name: "AI voice assistant", availability: [false, false, false] },
      { name: "Automatic review requests", availability: [false, false, false] },
      { name: "AI review replies", availability: [false, false, false] },
    ],
  },
  {
    label: "Run Your Business",
    features: [
      { name: "Quotes and invoices", availability: [false, false, true] },
      { name: "Job scheduling", availability: [false, false, true] },
      { name: "Get paid online", availability: [false, false, true] },
      { name: "Client portal", availability: [false, false, true] },
      { name: "CRM for leads and jobs", availability: [false, false, true] },
    ],
  },
];

const COMPARISON_SUMMARIES: ComparisonSummary[] = [
  { line1: "Expensive and manual", line2: "High retainers. Slow changes. No operations tools" },
  { line1: "Cheap, but DIY", line2: "You build it. You manage it. You figure it out" },
  { line1: "Good for operations", line2: "You still need separate marketing tools" },
];

// ---------------------------------------------------------------------------
// FAQ data
// ---------------------------------------------------------------------------

const FAQ_ITEMS: FAQItem[] = [
  {
    question: "Is Rebolt an agency?",
    answer:
      "No. Rebolt is software. But it does the work many agencies charge for. Your website, SEO, reviews, social media, and automations all run inside one platform.",
  },
  {
    question: "How is Rebolt different from Jobber?",
    answer:
      "Jobber helps you run operations. Rebolt helps you get more leads and run operations. Website, SEO, AI voice, CRM, scheduling, quotes, invoices, and payments live in one place.",
  },
  {
    question: "Do I still need a marketing agency?",
    answer:
      "Usually, no. Rebolt replaces most of the manual marketing work agencies charge for. That includes your website, local SEO, review workflows, and social media publishing.",
  },
  {
    question: "Can I edit my website?",
    answer: "Yes. You can update text, upload photos, add pages, and make changes without needing a developer.",
  },
  {
    question: "Do I own my domain?",
    answer: "Yes. Your domain stays yours. Always.",
  },
  {
    question: "What happens to my current website?",
    answer:
      "We move your domain to your new Rebolt site. Your old site redirects to the new one so visitors land in the right place.",
  },
  {
    question: "How long until I see results?",
    answer:
      "It depends on your market and competition. Some businesses see movement within a few weeks. Many see meaningful progress within 30 to 60 days.",
  },
  {
    question: "Is there a contract?",
    answer: "No. There are no long-term contracts. You can cancel anytime.",
  },
  {
    question: "Can my team use this in the field?",
    answer:
      "Yes. Rebolt includes mobile workflows for field teams so they can manage jobs, updates, and customer activity on the go.",
  },
  {
    question: "Can I export my website?",
    answer:
      "You always own your content and images. Because the site is tied into the Rebolt platform, it is not designed as a one-click export product.",
  },
];

// ---------------------------------------------------------------------------
// page
// ---------------------------------------------------------------------------

const BASE_URL = process.env.NEXT_PUBLIC_API_HOST ?? "https://rebolthq.com";

export default function PricingPage() {
  return (
    <>
      <NextSeo
        title="Pricing Plans | Rebolt"
        description="Rebolt replaces your marketing agency, website tools, and CRM. One platform built for home service businesses. See plans starting at $99/mo."
        canonical={`${BASE_URL}/pricing`}
        openGraph={{
          url: `${BASE_URL}/pricing`,
          title: "Pricing Plans | Rebolt",
          description:
            "Rebolt replaces your marketing agency, website tools, and CRM. One platform built for home service businesses. See plans starting at $99/mo.",
          images: [{ url: `${BASE_URL}/marketing-pages/OGImage.png`, width: 1200, height: 630 }],
          type: "website",
        }}
        twitter={{
          cardType: "summary_large_image",
        }}
      />

      <Navbar solidBackground />

      <main className="overflow-hidden bg-clay-100 relative">
        <div className="flex flex-col items-center justify-center relative">
          <PricingHeroSection plans={SUBSCRIPTION_PLANS} voiceAgent={VOICE_AGENT_ADDON} />

          <ComparisonSection competitors={COMPETITORS} groups={COMPARISON_GROUPS} summaries={COMPARISON_SUMMARIES} />

          <ReviewsSection />

          <FAQSection items={FAQ_ITEMS} />

          <Footer />
        </div>
      </main>
    </>
  );
}
